/* eslint-disable new-cap */
/* eslint-disable max-lines-per-function */
import React, { useEffect } from 'react'
import { Downgraded, useHookstate } from '@hookstate/core'
import { Grid, Typography } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import { getDispatchByClosedStatus } from './services/index'
import { IDespacthStatisticGraphProps } from '../../interfaces/IDispacthStatistic'
import { getDispatchByDispatchGroup } from '../DispatchByLocationCharts/services'
import ColumnChartVertical from '~/components/ColumnChartMui/VerticalChart'
import { useMarkerState } from '~/features/Map/stores/MapState'

type PayloadProps = {
  readonly withSolvedThirdParty?: boolean
  readonly exludeClosed?: boolean
}

type ResponseProps = ReadonlyArray<{
  readonly item: string
  readonly value: number
}>

export default function DispatchByTimeAndStatusCharts({
  loading,
  searched,
  agencyId,
  deviceId,
  operationId,
  dispatchGroupId,
  category,
  type,
  subtype,
  dateStart,
  dateFinish,
  thirdParty,
  refresh,
  consummated,
  statusIds,
  cityIds,
  groupSetId,
  location,
  closedStatusClassId,
  closedStatusId,
  qualifierOptions,
  expandFilterSearch,
  operationUnitId,
  phone,
  requester,
  userId,
  districtIds,
  geographicalArea,
  startTime,
  endTime,
}: IDespacthStatisticGraphProps) {
  const { translate } = useLang()
  const closedStatusData = useHookstate<ResponseProps>([])
  const groupData = useHookstate<ResponseProps>([])

  const payload = useHookstate<PayloadProps>({
    withSolvedThirdParty: thirdParty,
    exludeClosed: false,
  })
  const closedStatusDataTotal = useHookstate<number>(0)
  const groupDataTotal = useHookstate<number>(0)

  useEffect(() => {
    closedStatusDataTotal.set(0)
    groupDataTotal.set(0)
    payload.set({ withSolvedThirdParty: thirdParty, exludeClosed: false })
    loading.set(true)
    getDispatchByClosedStatus(
      agencyId,
      deviceId,
      operationId,
      dispatchGroupId,
      category,
      type,
      subtype,
      dateStart,
      dateFinish,
      payload.get(),
      consummated,
      statusIds,
      cityIds,
      groupSetId,
      location,
      closedStatusClassId,
      closedStatusId,
      qualifierOptions,
      operationUnitId,
      phone,
      geographicalArea,
      requester,
      userId,
      districtIds,
      startTime,
      endTime,
    )
      .then((response) => {
        const result = response.map((responseData, index) => {
          closedStatusDataTotal.set(
            closedStatusDataTotal.get() + responseData.total
          )
          return { item: responseData.name, value: responseData.total }
        })
        return closedStatusData.set(result)
      })
      .finally(() => {
        loading.set(false)
      })

    getDispatchByDispatchGroup({
      agencyId: agencyId,
      deviceId: deviceId,
      operationId: operationId,
      dispatchGroupId: dispatchGroupId,
      categoryIds: category,
      typeIds: type,
      subtypeIds: subtype,
      startDate: dateStart,
      endDate: dateFinish,
      payload: payload.get(),
      consummated: consummated,
      statusIds: statusIds,
      cityIds: cityIds,
      districtIds: districtIds,
      location: location,
      closedStatusClassId: closedStatusClassId,
      closedStatusId: closedStatusId,
      qualifierOptions: qualifierOptions,
      groupSetId: groupSetId,
      operationUnitId: operationUnitId,
      phone,
      geographicAreas: geographicalArea,
      requester,
      userId,
      startTime: startTime,
      endTime: endTime,
    })
      .then((response) => {
        const result = response.map((responseData) => {
          groupDataTotal.set(groupDataTotal.get() + responseData.total)
          return { item: responseData.name, value: responseData.total }
        })
        return groupData.set(result)
      })
      .finally(() => {
        loading.set(false)
      })
  }, [refresh])

  useEffect(() => {
    if (!searched) {
      groupData.set([])
      closedStatusData.set([])
    }
  }, [searched])

  return (
    <Grid
      container
      xs={12}
      sx={{
        display: 'flex',
        paddingX: 3,
        paddingTop: 3,
        justifyContent: 'space-between',
      }}
    >
      <Grid item xs={12} hidden={!expandFilterSearch} paddingBottom={1}>
        <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
          {translate('Data by Status')}
        </Typography>
      </Grid>

      <Grid item xs={12} hidden={!expandFilterSearch}>
        <ColumnChartVertical
          data={closedStatusData.attach(Downgraded).get()}
          title={'Dispatch x Closed Status'}
          subtitle={`Total: ${Intl.NumberFormat('pt-BR').format(
            closedStatusDataTotal.get()
          )}`}
          typeTotal="Status"
          colorBar="tertiary"
        />
      </Grid>
      <Grid
        item
        xs={12}
        hidden={!expandFilterSearch}
        paddingBottom={1}
        paddingTop={3}
      >
        <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
          {translate('Dispatch by Dispatch Group')}
        </Typography>
      </Grid>
      <Grid item xs={12} hidden={!expandFilterSearch}>
        <ColumnChartVertical
          data={groupData.attach(Downgraded).get()}
          title={'Dispatch x Dispatch Group'}
          subtitle={`Total: ${Intl.NumberFormat('pt-BR').format(
            groupDataTotal.get()
          )}`}
          typeTotal="Dispatch Group"
        />
      </Grid>
    </Grid>
  )
}
